import { useState, useCallback } from "react";
import { toast } from "../components/toast";

interface AsyncState {
  loading: boolean;
  error: Error | null;
}

interface ExecuteOptions {
  successMessage?: string;
}

interface UseAsyncReturn extends AsyncState {
  execute: <T>(asyncFn: () => Promise<T>, options?: ExecuteOptions) => Promise<T | void>;
  reset: () => void;
}

export function useAsync(): UseAsyncReturn {
  const [state, setState] = useState<AsyncState>({
    loading: false,
    error: null,
  });

  const execute = useCallback(async <T>(asyncFn: () => Promise<T>, options?: ExecuteOptions) => {
    setState({ loading: true, error: null });
    try {
      const result = await asyncFn();
      setState({ loading: false, error: null });
      if (options?.successMessage) {
        toast.success(options.successMessage);
      }
      return result;
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err));
      toast.error(error.message);
      setState({ loading: false, error });
      throw error;
    }
  }, []);

  const reset = useCallback(() => {
    setState({ loading: false, error: null });
  }, []);

  return { ...state, execute, reset };
}
